import { MicMutedIcon, MicUnmutedIcon, StarIcon } from "@minervaproject/fds";
import clsx from "clsx";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { ParticipantInfoMenu } from "./participant_info_menu";
import { ParticipantInfoMenuIcon } from "./participant_info_menu_icon";
import { SignalsAndReactions } from "./signals_and_reactions";

import { AvIssueIcon } from "components/av/av_issue_icon";
import { ParticipantAv } from "components/av/participant_av";
import { BreakoutGroupIndicator } from "components/participant_bar/breakout_group_indicator";
import { PollResponsesIndicator } from "components/participant_bar/poll_responses_indicator";
import { TalkTimeOverlay } from "components/participant_bar/talk_time_overlay";
import { useAppSelector } from "hooks/redux_hooks";
import { PROFILE_PROPERTIES, useUserProperties } from "hooks/use_user_properties";
import { SizeType } from "shared/types";
import { classroomSelectors } from "store/index";
import { selectEffectivePttMode } from "store/selectors/classroom";
import { longDisplayName, shortDisplayName } from "utils/user";

const EXPANDED_WIDTH = 267;

function ParticipantTileContent({
  userId,
  canControlClassroom,
  currentUserCanControlClassroom,
  isSelf,
  profilePhotoSize,
  signalSize,
  includeVideo = true,
  isMinimal,
  isSquircle = false,
  index,
  participantCount,
  isVertical = false,
  noHover = false,
}: {
  userId: string;
  canControlClassroom: boolean;
  currentUserCanControlClassroom: boolean;
  isSelf: boolean;
  profilePhotoSize: SizeType;
  signalSize: SizeType;
  includeVideo?: boolean;
  isMinimal: boolean;
  isSquircle?: boolean;
  index: number;
  participantCount?: number; // only needed for horizontal PBs
  isVertical?: boolean;
  noHover?: boolean;
}) {
  const user = useUserProperties(userId, PROFILE_PROPERTIES.concat(["is_present", "is_speaking"]));

  const cameraError = useAppSelector(classroomSelectors.selectUserCameraError(userId));
  const micError = useAppSelector(classroomSelectors.selectUserMicError(userId));
  const avAvailable = useAppSelector(classroomSelectors.selectUserHasAvailableAv(userId));
  const avIssue = !!micError || !avAvailable;
  const poorConnection = useAppSelector(classroomSelectors.selectUserHasPoorConnection(userId));
  const bgColor = avIssue ? "bg-red-shade-20" : "bg-gradient-to-bottom";
  const pttMode = useAppSelector(selectEffectivePttMode(userId));
  const mutedAudio = pttMode && user?.is_speaking === false;
  const [showParticipantInfoMenu, setShowParticipantInfoMenu] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isParticipantHovered, setIsParticipantHovered] = useState(false);
  const [isPimHovered, setIsPimHovered] = useState(false);
  const [transformOrigin, setTransformOrigin] = useState(
    isVertical && index !== 0 ? "bottom left" : "top left",
  );

  useEffect(() => {
    if (index !== 0 || isMinimal) return;

    const resizeObserver = new ResizeObserver((entries) => {
      // The participant component should grow to 267px wide when hovered
      const scale = EXPANDED_WIDTH / entries[0].contentRect.width;
      document.documentElement.style.setProperty("--participant-scale", `${scale}`);
    });

    let containerElement: Element | null | undefined;

    const getElement = () => {
      containerElement = containerRef?.current;
      if (containerElement) {
        resizeObserver.observe(containerElement);
      } else {
        setTimeout(getElement, 100);
      }
    };
    getElement();

    return () => {
      if (containerElement) {
        resizeObserver.unobserve(containerElement);
      }
    };
  }, [index, isMinimal]);

  useEffect(() => {
    if (!isParticipantHovered && !isPimHovered) {
      setShowParticipantInfoMenu(false);
    }
  }, [isParticipantHovered, isPimHovered]);

  useLayoutEffect(() => {
    if (containerRef.current && !isVertical && participantCount !== undefined) {
      // If there isn't enough room to expand to the right when hovered, then expand to the left instead
      const reverseIndex = participantCount - index - 1;
      if (reverseIndex * containerRef.current.clientWidth < EXPANDED_WIDTH && index !== 0) {
        setTransformOrigin("top right");
      }
    }
  }, [index, isVertical, participantCount]);

  if (!user) {
    return null;
  }

  return (
    <>
      <div
        className={clsx("participant-tile-content w-full h-full", {
          "is-hovered": showParticipantInfoMenu,
          "is-minimal": isMinimal,
          "no-hover": noHover,
        })}
        style={{
          transformOrigin,
        }}
        ref={containerRef}
        onMouseEnter={() => !noHover && setIsParticipantHovered(true)}
        onMouseLeave={() => !noHover && setTimeout(() => setIsParticipantHovered(false), 100)}
        data-testid={`participant-tile-content-${userId}`}
      >
        <ParticipantAv
          userId={userId}
          isSelf={isSelf}
          profilePhotoSize={profilePhotoSize}
          includeVideo={includeVideo}
          isSquircle={isSquircle || isMinimal}
        />
        {!isMinimal && (
          <>
            <SignalsAndReactions userId={userId} size={signalSize} />
            <BreakoutGroupIndicator userId={userId} size={signalSize} />
            <button
              type="button"
              className={clsx(
                "button-reset absolute top-0 bottom-0 left-0 right-0 flex items-end",
                {
                  "cursor-pointer": user.is_present && !noHover,
                },
              )}
              style={{ opacity: user.is_present ? 1 : 0.5, zIndex: 100 }}
              aria-label="Participant info menu"
              onClick={() => !noHover && setShowParticipantInfoMenu(!showParticipantInfoMenu)}
              disabled={!user.is_present}
              data-testid={`pim-button-${userId}`}
            >
              <div
                className={`flex items-center justify-center w-full py1 px2 border-box ${bgColor} button-contents`}
                style={
                  isSquircle && !isParticipantHovered && !isPimHovered
                    ? {
                        borderBottomLeftRadius: "18px",
                        borderBottomRightRadius: "18px",
                      }
                    : undefined
                }
              >
                <div className="hide-on-hover w-full">
                  <div className="flex items-center justify-center">
                    {canControlClassroom ? (
                      <StarIcon
                        width={10}
                        height={10}
                        color="white"
                        className="flex-none"
                        style={{ paddingTop: "2px" }}
                      />
                    ) : null}
                    <span className="body-xs bold text-white mx1 truncate">
                      {shortDisplayName(user)}
                    </span>
                    {avIssue && micError ? (
                      <AvIssueIcon
                        title="This user has an issue with their microphone."
                        size={12}
                      />
                    ) : avIssue ? (
                      <AvIssueIcon title="This user's A/V is not available." size={12} />
                    ) : poorConnection ? (
                      <AvIssueIcon
                        poorConnection
                        title="This user has a poor connection."
                        size={12}
                      />
                    ) : cameraError ? (
                      <AvIssueIcon title="This user has an issue with their camera." size={12} />
                    ) : null}
                  </div>
                </div>
                <div className="show-on-hover">
                  <div className="flex items-center">
                    {!user.is_present ? null : mutedAudio ? (
                      <span
                        className="bg-red-shade-20 circle flex items-center justify-center"
                        style={{
                          width: "16px",
                          height: "16px",
                        }}
                      >
                        <MicMutedIcon width={12} height={12} color="white" />
                      </span>
                    ) : (
                      <MicUnmutedIcon width={16} height={16} color="white" />
                    )}
                    {canControlClassroom ? (
                      <StarIcon
                        width={10}
                        height={10}
                        color="white"
                        className="flex-none ml1"
                        style={{ paddingTop: "2px" }}
                      />
                    ) : null}
                    <span className="body-xs bold text-white mx1 truncate">
                      {longDisplayName(user)}
                    </span>
                  </div>
                </div>
              </div>
              {user.is_present && (
                <span className="show-on-hover">
                  <span className="absolute bottom-0 right-0 py1 px3">
                    <ParticipantInfoMenuIcon
                      avIssue={avIssue}
                      micError={!!micError}
                      cameraError={!!cameraError}
                      poorConnection={poorConnection}
                    />
                  </span>
                </span>
              )}
            </button>
          </>
        )}
        {currentUserCanControlClassroom && <TalkTimeOverlay userId={userId} />}
        {!noHover && (
          <PollResponsesIndicator
            userId={userId}
            currentUserCanControlClassroom={currentUserCanControlClassroom}
            showBar={!isParticipantHovered && !showParticipantInfoMenu}
            showDropdown={isParticipantHovered && !showParticipantInfoMenu}
            index={index}
            isVertical={isVertical}
            transformTopRight={transformOrigin === "top right"}
          />
        )}
      </div>
      {showParticipantInfoMenu && (
        <div
          onMouseEnter={() => setIsPimHovered(true)}
          onMouseLeave={() => setTimeout(() => setIsPimHovered(false), 100)}
        >
          <ParticipantInfoMenu
            userId={userId}
            index={index}
            isVertical={isVertical}
            transformTopRight={transformOrigin === "top right"}
          />
        </div>
      )}
    </>
  );
}

export { ParticipantTileContent };
